import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { useUser } from '@clerk/clerk-react'
import { getUserFromClerkId } from '../hooks/useUser'
import { FiChevronDown, FiAlertCircle, FiAlertTriangle, FiMessageSquare, FiFlag, FiBookOpen, FiSearch, FiSmartphone, FiUsers, FiArrowRight, FiMail, FiLink, FiInbox } from 'react-icons/fi'
import Button from '../components/Button'
import EmergencyScamWizard from '../components/EmergencyScamWizard'
import { useAppTour } from '@/contexts/AppTourContext'

export const Route = createFileRoute('/_auth/help')({
  component: RouteComponent,
})

interface FAQItem {
  question: string
  answer: React.ReactNode
}

interface FAQSection {
  title: string
  icon: React.ReactNode
  items: FAQItem[]
}

function FAQAccordion({ item, isOpen, onToggle }: { item: FAQItem; isOpen: boolean; onToggle: () => void }) {
  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <button
        className="group flex w-full cursor-pointer items-start justify-between gap-4 py-5 text-left focus:outline-none"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span className="text-base font-medium leading-snug text-slate-900 transition-colors group-hover:text-violet-700 sm:text-lg">
          {item.question}
        </span>
        <span className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full transition-all duration-200 ${isOpen ? 'rotate-180 bg-violet-100 text-violet-600' : 'bg-slate-100 text-slate-400'}`}>
          <FiChevronDown className="w-3.5 h-3.5" />
        </span>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-out ${isOpen ? 'max-h-[500px] pb-5 opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="pr-8 text-base leading-relaxed text-slate-600">
          {item.answer}
        </div>
      </div>
    </div>
  )
}

function SupportCard({
  icon,
  title,
  description,
  action,
  onClick,
  to,
  dataTourId
}: {
  icon: React.ReactNode
  title: string
  description: string
  action: string
  onClick?: () => void
  to?: string
  dataTourId?: string
}) {
  const content = (
    <>
      <div className="w-11 h-11 rounded-[13px] flex items-center justify-center mb-4 transition-colors bg-slate-100 text-slate-600 group-hover:bg-violet-50 group-hover:text-violet-600">
        {icon}
      </div>
      <h3 className="mb-1.5 text-lg font-semibold text-slate-900">
        {title}
      </h3>
      <p className="mb-4 flex-1 text-sm leading-relaxed text-slate-500">
        {description}
      </p>
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-violet-700">
        {action}
        <FiArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
      </span>
    </>
  )

  const cardClasses = `group flex cursor-pointer flex-col rounded-2xl bg-white p-6 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] transition-shadow duration-200 hover:shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_4px_12px_rgba(0,0,0,0.07)]`

  if (to) {
    return (
      <a href={to} data-tour-id={dataTourId} className={cardClasses}>
        {content}
      </a>
    )
  }

  return (
    <button onClick={onClick} data-tour-id={dataTourId} className={`${cardClasses} text-left`}>
      {content}
    </button>
  )
}

function SituationLink({
  icon,
  text,
  hint,
  onClick
}: {
  icon: React.ReactNode
  text: string
  hint?: string
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className="group flex min-h-[72px] cursor-pointer items-center gap-4 rounded-xl border border-slate-200/60 bg-white p-5 text-left transition-all hover:border-slate-300 hover:shadow-sm"
    >
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500 transition-colors group-hover:bg-violet-50 group-hover:text-violet-600">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <span className="block text-base font-medium leading-snug text-slate-800">{text}</span>
        {hint && <span className="block mt-0.5 text-sm text-slate-400">{hint}</span>}
      </div>
      <FiArrowRight className="h-4 w-4 flex-shrink-0 text-slate-300 transition-colors group-hover:text-violet-500" />
    </button>
  )
}

function RouteComponent() {
  const { user: clerkUser } = useUser()
  const { data: appUser } = getUserFromClerkId(clerkUser?.id ?? '', { enabled: !!clerkUser?.id })
  const navigate = useNavigate()
  const { startTour } = useAppTour()

  const faqSections: FAQSection[] = [
    {
      title: "Getting Started",
      icon: <FiBookOpen className="w-5 h-5" />,
      items: [
        {
          question: "What's the best way to use GranGuard?",
          answer: (
            <div className="space-y-3">
              <p>We recommend a steady, sustainable approach:</p>
              <ul className="space-y-2 ml-1">
                <li className="flex gap-2">
                  <span className="text-violet-500 mt-1">•</span>
                  <span><strong>Start with the basics</strong> — Complete your Scam Defense Fundamentals first</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-violet-500 mt-1">•</span>
                  <span><strong>Build a habit</strong> — Check your daily tip and complete one new lesson each week</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-violet-500 mt-1">•</span>
                  <span><strong>Use your tools</strong> — Run a Digital Footprint Scan every few months</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-violet-500 mt-1">•</span>
                  <span><strong>Stay informed</strong> — Check scam alerts when you see something suspicious</span>
                </li>
              </ul>
            </div>
          ),
        },
        {
          question: "How do I track my progress?",
          answer:
            "Your dashboard shows your journey across all six competencies. As you complete training content, you'll earn points and see your skills grow in each area. It's easy to spot which areas you've mastered and where you might want to focus next.",
        },
        {
          question: "I'm already pretty knowledgeable about scams. Where should I start?",
          answer:
            "That's wonderful! Jump straight into intermediate or advanced content in areas you want to strengthen. Even experienced users often discover tactics they weren't aware of. The Scam Library is also great for staying current on emerging threats.",
        },
        {
          question: "Should I share what I'm learning with family?",
          answer:
            "Absolutely — it's one of the best things you can do. Building a network of informed people who can verify suspicious situations with each other is powerful protection. Share interesting examples or tips you learn. It helps keep everyone safer.",
        },
      ],
    },
    {
      title: "Technical Questions",
      icon: <FiSmartphone className="w-5 h-5" />,
      items: [
        {
          question: "What devices can I use GranGuard on?",
          answer:
            "GranGuard works on any device with a modern web browser — computers, tablets, and smartphones. For the best experience, we recommend Chrome, Firefox, Safari, or Edge kept up to date.",
        },
        {
          question: "Do I need to download any software?",
          answer:
            "No downloads needed. GranGuard is entirely web-based. Simply visit our website and sign in — everything runs right in your browser.",
        },
        {
          question: "What if I'm having trouble accessing the platform?",
          answer: (
            <div className="space-y-3">
              <p>Try these steps in order:</p>
              <ol className="space-y-2 ml-1">
                <li className="flex gap-2">
                  <span className="text-violet-500 font-medium">1.</span>
                  <span>Refresh your browser page</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-violet-500 font-medium">2.</span>
                  <span>Clear your browser's cache and cookies</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-violet-500 font-medium">3.</span>
                  <span>Try a different browser</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-violet-500 font-medium">4.</span>
                  <span>Double-check your login credentials</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-violet-500 font-medium">5.</span>
                  <span>If issues persist, reach out to our support team</span>
                </li>
              </ol>
            </div>
          ),
        },
        {
          question: "Can I use GranGuard offline?",
          answer:
            "The main platform needs an internet connection, but your physical Welcome Kit provides offline reference materials you can keep handy. You can also print articles and guides for offline reading.",
        },
      ],
    },
  ]

  const [openKey, setOpenKey] = useState<string | null>(null)
  const [isEmergencyWizardOpen, setIsEmergencyWizardOpen] = useState(false)
  const [activeFaqSection, setActiveFaqSection] = useState(0)
  const location = useLocation()

  useEffect(() => {
    if (location.hash === '#faq') {
      const el = document.getElementById('faq')
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }, [location.hash])

  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">

        {/* Header */}
        <header className="pb-10 pt-12 sm:pt-14">
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            How can we help?
          </h1>
          <p className="mt-2 text-lg text-slate-500">
            Get answers, report concerns, or find immediate help.
          </p>
        </header>

        {/* Common Situations */}
        <section className="mb-8 rounded-2xl bg-white p-6 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] sm:p-8">
          <div className="mb-5">
            <h2 className="text-xl font-semibold text-slate-900 sm:text-2xl">
              Common situations
            </h2>
            <p className="mt-1 text-base text-slate-500">
              Not sure where to start? Choose what describes your situation.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <SituationLink
              icon={<FiMail className="w-5 h-5" />}
              text="I received a suspicious email, text, or call"
              hint="We'll help you figure out if it's real"
              onClick={() => navigate({ to: '/tools/scam-check/situation', search: { q: undefined, invitationcode: undefined, __clerk_ticket: undefined } })}
            />
            <SituationLink
              icon={<FiAlertCircle className="w-5 h-5" />}
              text="Something doesn't feel right about a person or offer"
              hint="Describe the situation and we'll assess it"
              onClick={() => navigate({ to: '/tools/scam-check/situation', search: { q: undefined, invitationcode: undefined, __clerk_ticket: undefined } })}
            />
            <SituationLink
              icon={<FiLink className="w-5 h-5" />}
              text="I clicked a link or shared information I shouldn't have"
              hint="Get step-by-step recovery guidance"
              onClick={() => setIsEmergencyWizardOpen(true)}
            />
            <SituationLink
              icon={<FiAlertTriangle className="w-5 h-5" />}
              text="I think I've been scammed"
              hint="Immediate actions to protect yourself"
              onClick={() => setIsEmergencyWizardOpen(true)}
            />
          </div>
        </section>

        {/* Quick Actions */}
        <section className="mb-12">
          <div className="mb-5">
            <h2 className="text-xl font-semibold text-slate-900 sm:text-2xl">Quick actions</h2>
            <p className="mt-1 text-base text-slate-500">Choose the fastest way to get support.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <SupportCard
              icon={<FiSearch className="w-5 h-5" />}
              title="Check Something Suspicious"
              description="Received something that doesn't seem right? Our AI will help you figure out if it's a scam."
              action="Start assessment"
              to="/tools/scam-check/situation"
            />
            <SupportCard
              icon={<FiFlag className="w-5 h-5" />}
              title="Report a Scam"
              description="Help protect others by sharing details of a scam you've encountered."
              action="Report now"
              to="/report-a-scam"
            />
            <SupportCard
              icon={<FiMessageSquare className="w-5 h-5" />}
              title="Contact Support"
              description="Have a question or need assistance? Our team is here to help."
              action="Get in touch"
              to="/contact-support"
              dataTourId="tour-support"
            />
            <SupportCard
              icon={<FiInbox className="w-5 h-5" />}
              title="Messages"
              description="View and track your support conversations."
              action="View messages"
              to="/messages"
            />
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="pb-12">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-slate-900 sm:text-2xl">
              Frequently asked questions
            </h2>
          </div>

          {/* Category tabs */}
          <div className="mb-6">
            <div className="inline-flex rounded-xl bg-slate-100/80 p-1">
              {faqSections.map((section, idx) => (
                <button
                  key={section.title}
                  onClick={() => {
                    setActiveFaqSection(idx)
                    setOpenKey(null)
                  }}
                  className={`flex cursor-pointer items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-all duration-200 ${
                    activeFaqSection === idx
                      ? 'bg-white text-slate-900 shadow-[0_1px_3px_rgba(0,0,0,0.08)]'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {section.icon}
                  {section.title}
                </button>
              ))}
            </div>
          </div>

          {/* FAQ content */}
          <div className="rounded-2xl bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)]">
            <div className="px-6 sm:px-8">
              {faqSections[activeFaqSection].items.map((faq, itemIdx) => {
                const key = `${activeFaqSection}-${itemIdx}`
                return (
                  <FAQAccordion
                    key={key}
                    item={faq}
                    isOpen={openKey === key}
                    onToggle={() => setOpenKey(openKey === key ? null : key)}
                  />
                )
              })}
            </div>
          </div>
          <button
            type="button"
            onClick={startTour}
            className="mt-4 min-h-11 cursor-pointer text-sm font-medium text-violet-700 underline underline-offset-4 hover:text-violet-900 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-violet-500"
          >
            Replay the quick introduction
          </button>
        </section>

        {/* Still need help */}
        <section className="pb-12">
          <div className="rounded-2xl bg-white p-8 text-center shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] sm:p-10">
            <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-[13px] bg-violet-50 text-violet-600">
              <FiUsers className="h-6 w-6" />
            </div>
            <h3 className="mb-2 text-xl font-semibold text-slate-900 sm:text-2xl">
              Still have questions?
            </h3>
            <p className="mx-auto mb-6 max-w-lg text-base leading-relaxed text-slate-500">
              Our support team is happy to help. There's no such thing as a silly question — if something's on your mind, just ask.
            </p>
            <Button
              to="/contact-support"
              variant="primary"
              size="large"
              className="rounded-xl"
            >
              Contact support
            </Button>
          </div>
        </section>

        {/* Resources */}
        <section className="pb-16">
          <h3 className="mb-4 text-lg font-semibold text-slate-900">
            Helpful resources
          </h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <a
              href="/learn"
              className="group flex items-center gap-4 rounded-2xl bg-white p-5 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] transition-shadow hover:shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_4px_12px_rgba(0,0,0,0.07)]"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-[11px] bg-violet-50 text-violet-600">
                <FiBookOpen className="h-5 w-5" />
              </div>
              <div>
                <div className="text-base font-medium text-slate-900">Learning Centre</div>
                <div className="text-sm text-slate-500">Browse guides and tutorials</div>
              </div>
            </a>
            <a
              href="/scam-library"
              className="group flex items-center gap-4 rounded-2xl bg-white p-5 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] transition-shadow hover:shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_4px_12px_rgba(0,0,0,0.07)]"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-[11px] bg-violet-50 text-violet-600">
                <FiAlertCircle className="h-5 w-5" />
              </div>
              <div>
                <div className="text-base font-medium text-slate-900">Scam Library</div>
                <div className="text-sm text-slate-500">Learn about common scam types</div>
              </div>
            </a>
          </div>
        </section>

      </div>

      {/* Emergency Scam Wizard Modal */}
      <EmergencyScamWizard
        isOpen={isEmergencyWizardOpen}
        onClose={() => setIsEmergencyWizardOpen(false)}
        userMarket={appUser?.market}
      />
    </div>
  )
}
